import { applyRetainedQueryParams } from '@wix/thunderbolt-commons'

export const getRelativeEncodedUrl = (url: string, baseUrl: string) =>
	getRelativeUrlData(url, baseUrl).relativeEncodedUrl

export const getRelativeUrlData = (
	url: string,
	baseUrl: string
): {
	relativePathnameParts: Array<string>
	relativeUrl: string
	relativeEncodedUrl: string
} => {
	const relativePathname = getRelativePathname(url, baseUrl)
	const relativePathnameParts = getPathnameDecodedParts(relativePathname)
	const relativeUrl = pathnamePartsToRelativeUrl(relativePathnameParts)
	const relativeEncodedUrl = pathnamePartsToRelativeUrl(getPathnameParts(relativePathname))

	return {
		relativePathnameParts,
		relativeUrl,
		relativeEncodedUrl,
	}
}

const getRelativePathname = (url: string, baseUrl: string): string => {
	const parsedUrl = new URL(url, `${baseUrl}/`)
	const parsedBaseUrl = new URL(baseUrl)

	return parsedUrl.pathname.replace(parsedBaseUrl.pathname, '')
}

const getPathnameDecodedParts = (relativePathname: string) => {
	const cleanPath = removeLeadingAndTrailingSlash(relativePathname)

	return decodeUriComponentIfEncoded(cleanPath).split('/')
}

const pathnamePartsToRelativeUrl = (pathnameParts: Array<string>): string => `./${pathnameParts.join('/')}`

const getPathnameParts = (relativePathname: string) => removeLeadingAndTrailingSlash(relativePathname).split('/')

const removeLeadingAndTrailingSlash = (str: string): string => /^\/?(.*?)\/?$/.exec(str)![1]

export const getRelativeUrl = (url: string, baseUrl: string) => getRelativeUrlData(url, baseUrl).relativeUrl

export const decodeUriComponentIfEncoded = (uriStr: string) => {
	try {
		return decodeURIComponent(uriStr)
	} catch (e) {
		return uriStr
	}
}

const trimSlashes = (path: string) => path.replace(/^\/+|\/+$/g, '')

// A Wix Pages proxy route serves a page at another address (the x-wix-proxy-base-path request header, exposed as
// viewerModel.proxyBasePath), while the server rendered requestUrl. That address can't be routed, so route requestUrl
// instead, keeping the address's query and hash (requestUrl's query is the one from the first load).
export const toRoutableUrl = (href: string, proxyBasePath: string | undefined, requestUrl: string): string => {
	if (!proxyBasePath) {
		return href
	}

	try {
		const addressUrl = new URL(href)
		// Compare both decoded - a non-ASCII proxy path arrives percent-encoded in the header
		const addressPath = trimSlashes(decodeUriComponentIfEncoded(addressUrl.pathname))
		if (addressPath !== trimSlashes(decodeUriComponentIfEncoded(proxyBasePath))) {
			return href
		}

		const routableUrl = new URL(requestUrl)
		routableUrl.search = addressUrl.search
		routableUrl.hash = addressUrl.hash
		return routableUrl.href
	} catch (e) {
		return href
	}
}

// Whether the address is a proxied address (see toRoutableUrl) routed as targetUrl.
// Navigating to the page such an address shows must keep the address.
export const isAddressRoutedAs = (
	addressUrl: URL,
	targetUrl: URL,
	proxyBasePath: string | undefined,
	requestUrl: string
): boolean => {
	const routableUrl = toRoutableUrl(addressUrl.href, proxyBasePath, requestUrl)
	if (routableUrl === addressUrl.href) {
		return false
	}

	const routableAddressUrl = applyRetainedQueryParams(addressUrl, routableUrl)
	const retainedTargetUrl = applyRetainedQueryParams(addressUrl, targetUrl.href)
	routableAddressUrl.searchParams.sort()
	retainedTargetUrl.searchParams.sort()
	return routableAddressUrl.toString() === retainedTargetUrl.toString()
}

export const getFullUrlWithoutQueryParams = (url: string, externalBaseUrl: string): string => {
	const relativeUrl = getRelativeUrl(url, externalBaseUrl)
	const isHomePageUrl = relativeUrl === './'
	const replaceStr = isHomePageUrl ? externalBaseUrl : `${externalBaseUrl}/`

	return relativeUrl.replace('./', replaceStr)
}
